import { withRouter } from 'react-router-dom';
import React, { Component } from 'react';
import { Helmet } from 'react-helmet';
import axios from 'axios';

// Images
import logoImport from "../../images/logo.png";
const logo = typeof logoImport === 'object' ? logoImport.default : logoImport;

class MechanicKYC extends Component {
  constructor(props) {
    super(props);
    this.state = {
      mechanicName: '',
      mechanicPhone: '',
      
      // Agreement
      agreementText: '',
      hasReadAgreement: false,
      readTimeSeconds: 0,
      agreementAccepted: false,
      
      // Documents
      aadhaarFront: null,
      aadhaarBack: null,
      panCard: null,
      
      // Upload status
      uploadingAgreement: false,
      uploadingDocuments: false,
      agreementUploaded: false,
      documentsUploaded: false,
      
      // Messages
      errorMessage: '',
      successMessage: '',
      
      // Verification pending
      submittedForVerification: false,
      
      // Test account countdown
      autoVerifyCountdown: null,
      
      isClient: false
    };
    
    this.readTimer = null;
    this.countdownInterval = null;
  }

  async componentDidMount() {
    this.props.setShowChatBot(false);
    this.setState({ isClient: true });
    
    const { mechanicName, mechanicPhone } = this.props.location.state || {};
    
    if (!mechanicPhone) {
      // No mechanic info, redirect to login
      this.props.history.push('/mechanic-login');
      return;
    }
    
    this.setState({
      mechanicName: mechanicName || '',
      mechanicPhone: mechanicPhone
    });
    
    // Load agreement from backend
    try {
      const response = await axios.get('https://ridenrepair.com/api/get_mechanic_agreement');
      if (response.data.success) {
        this.setState({ agreementText: response.data.agreement_text });
      }
    } catch (error) {
      console.error('Error loading agreement:', error);
    }
  }

  componentWillUnmount() {
    if (this.readTimer) {
      clearInterval(this.readTimer);
    }
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
    }
  }

  // Agreement reading timer
  startReadingTimer = () => {
    if (this.readTimer) return; // Already started
    
    this.readTimer = setInterval(() => {
      this.setState((prevState) => {
        const newTime = prevState.readTimeSeconds + 1;
        if (newTime >= 30) {
          this.setState({ hasReadAgreement: true });
          clearInterval(this.readTimer);
        }
        return { readTimeSeconds: newTime };
      });
    }, 1000);
  };

  // Accept agreement (click to sign)
  handleAcceptAgreement = async () => {
    if (!this.state.hasReadAgreement) {
      this.setState({ errorMessage: 'Please read the agreement for at least 30 seconds' });
      return;
    }
    
    this.setState({ uploadingAgreement: true, errorMessage: '' });
    
    try {
      // Create personalized signed agreement
      const { agreementText, mechanicName, mechanicPhone, readTimeSeconds } = this.state;
      const signedAgreement = (agreementText || '')
        .replace(/{DATE}/g, new Date().toLocaleDateString())
        .replace(/{MECHANIC_NAME}/g, mechanicName)
        .replace(/{MECHANIC_PHONE}/g, mechanicPhone)
        .replace(/{ADDRESS}/g, 'As per records');
      
      const response = await axios.post('https://ridenrepair.com/api/sign_mechanic_agreement', {
        mechanic_phone: mechanicPhone,
        mechanic_name: mechanicName,
        read_time_seconds: readTimeSeconds,
        signed_agreement_text: signedAgreement,
        timestamp: Date.now()
      });
      
      if (response.status === 200) {
        this.setState({
          agreementAccepted: true,
          agreementUploaded: true,
          uploadingAgreement: false
        });

        // For KYC test phones, show document upload step instead of auto-approving
        const KYC_TEST_PHONES = ['9828857307', '9811039250'];
        if (KYC_TEST_PHONES.includes(mechanicPhone)) {
          // Stay on page — render will now show Step 2 (document upload)
          return;
        }

        // All mechanics must upload Aadhaar + PAN documents before verification
        // Stay on page — render will now show Step 2 (document upload)
        // Previously auto-approved here, now requiring document upload for everyone
      }
    } catch (error) {
      this.setState({ 
        errorMessage: 'Failed to sign agreement. Please try again.',
        uploadingAgreement: false
      });
    }
  };

  // Download agreement as text file (works on all devices including mobile)
  downloadAgreement = () => {
    const { agreementText, mechanicName, mechanicPhone } = this.state;
    
    // Replace placeholders
    const personalizedAgreement = (agreementText || '')
      .replace(/{DATE}/g, new Date().toLocaleDateString())
      .replace(/{MECHANIC_NAME}/g, mechanicName)
      .replace(/{MECHANIC_PHONE}/g, mechanicPhone)
      .replace(/{ADDRESS}/g, 'As per records');

    // iOS app: WKWebView ignores a programmatic <a download>, so bridge the text
    // to native, which writes a .txt and opens the iOS share sheet (Save to
    // Files / AirDrop / Mail). Flag is set ONLY by the iOS app — web and Android
    // fall through to the blob download below, unchanged.
    if (typeof window !== 'undefined' && window.__rnrIOSNativeUpload && window.ReactNativeWebView) {
      try {
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'NATIVE_SAVE_AGREEMENT',
          filename: `Mechanic_Agreement_${mechanicPhone}_${Date.now()}.txt`,
          text: personalizedAgreement,
        }));
      } catch (e) {}
      return;
    }

    // Create a Blob with the agreement text
    const blob = new Blob([personalizedAgreement], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    
    // Create a temporary anchor element and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = `Mechanic_Agreement_${mechanicPhone}_${Date.now()}.txt`;
    document.body.appendChild(link);
    link.click();
    
    // Clean up
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  handleFileChange = (field, event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        this.setState({ errorMessage: `${field} file size must be less than 10MB` });
        return;
      }
      this.setState({ [field]: file, errorMessage: '' });
    }
  };

  handleSubmitDocuments = async () => {
    const { mechanicPhone, aadhaarFront, aadhaarBack, panCard } = this.state;

    if (!aadhaarFront || !aadhaarBack || !panCard) {
      this.setState({ errorMessage: 'Please upload all three documents: Aadhaar Front, Aadhaar Back, and PAN Card' });
      return;
    }

    this.setState({ uploadingDocuments: true, errorMessage: '' });

    try {
      const formData = new FormData();
      formData.append('mechanic_phone', mechanicPhone);
      formData.append('timestamp', Date.now());
      formData.append('aadhaar_front', aadhaarFront);
      formData.append('aadhaar_back', aadhaarBack);
      formData.append('pan_card', panCard);

      // Explicit Authorization header — the global axios interceptor SHOULD
      // attach this from localStorage.authToken, but FormData multipart
      // uploads have occasionally slipped through without it. Defense-in-depth.
      const _kycToken = (() => { try { return localStorage.getItem('authToken') || ''; } catch (e) { return ''; } })();
      const response = await axios.post('https://ridenrepair.com/api/upload_mechanic_documents', formData, _kycToken ? { headers: { Authorization: `Bearer ${_kycToken}` } } : undefined);

      if (response.status === 200) {
        const KYC_TEST_PHONES = ['9828857307', '9811039250'];
        const isTestAccount = KYC_TEST_PHONES.includes(mechanicPhone);

        this.setState({
          documentsUploaded: true,
          submittedForVerification: true,
          successMessage: isTestAccount
            ? 'Documents uploaded successfully! Auto-verifying in 5 seconds...'
            : 'Documents uploaded successfully! Your account is pending verification.',
          errorMessage: ''
        });

        if (isTestAccount) {
          let countdown = 5;
          this.setState({ autoVerifyCountdown: countdown });

          this.countdownInterval = setInterval(() => {
            countdown--;
            if (countdown > 0) {
              this.setState({ autoVerifyCountdown: countdown });
            } else {
              clearInterval(this.countdownInterval);
              this.countdownInterval = null;
              this.setState({ autoVerifyCountdown: null });
            }
          }, 1000);

          setTimeout(async () => {
            if (this.countdownInterval) {
              clearInterval(this.countdownInterval);
              this.countdownInterval = null;
            }
            try {
              await axios.post('https://ridenrepair.com/api/approve_mechanic_verification', {
                mechanic_phone: mechanicPhone
              });
            } catch (error) {
              console.error('Error auto-approving test account:', error);
            }
            this.props.history.push('/mechanic-login');
          }, 5000);
        }
      }
    } catch (error) {
      this.setState({
        errorMessage: error.response?.data?.error || 'Failed to upload documents. Please try again.',
        uploadingDocuments: false
      });
    } finally {
      this.setState({ uploadingDocuments: false });
    }
  };

  render() {
    const { 
      isClient, 
      mechanicName,
      agreementText,
      agreementAccepted, 
      hasReadAgreement, 
      readTimeSeconds,
      uploadingAgreement,
      aadhaarFront,
      aadhaarBack,
      panCard,
      uploadingDocuments,
      submittedForVerification,
      errorMessage,
      successMessage,
      autoVerifyCountdown,
      mechanicPhone
    } = this.state;

    const KYC_TEST_PHONES = ['9828857307', '9811039250'];
    const isTestAccount = KYC_TEST_PHONES.includes(mechanicPhone);

    if (submittedForVerification) {
      return (
        <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>
        <div className="page-wraper account-wraper bg-gray py-100 px-20">
          <div className="my-account">
            <div className="logo">
              {isClient && <img src={logo} alt="" />}
            </div>
            <div style={{ textAlign: 'center', padding: '30px' }}>
              {isTestAccount && autoVerifyCountdown !== null ? (
                <>
                  <h3 style={{ color: '#28a745', marginBottom: '20px' }}>🔄 Auto-Verifying Account</h3>
                  <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#333' }}>
                    Thank you for submitting your documents!
                    <br/><br/>
                    Your test account will be automatically verified in:
                    <br/><br/>
                    <span style={{ fontSize: '32px', fontWeight: 'bold', color: '#f42222' }}>
                      {autoVerifyCountdown}
                    </span>
                    <br/><br/>
                    seconds...
                  </p>
                </>
              ) : isTestAccount ? (
                <>
                  <h3 style={{ color: '#28a745', marginBottom: '20px' }}>✅ Verification Complete</h3>
                  <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#333' }}>
                    Your account has been verified!
                    <br/><br/>
                    Redirecting to login page...
                  </p>
                </>
              ) : (
                <>
                  <h3 style={{ color: '#28a745', marginBottom: '20px' }}>✅ Verification Pending</h3>
                  <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#333' }}>
                    Thank you for submitting your documents!
                    <br/><br/>
                    Your account is currently under review by our team.
                    <br/><br/>
                    You will receive a notification once your account is verified.
                    <br/><br/>
                    This usually takes 24-48 hours.
                  </p>
                  <button 
                    className="btn btn-lg btn-primary w-100" 
                    style={{ marginTop: '20px', maxWidth: '300px' }}
                    onClick={() => this.props.history.push('/mechanic-login')}
                  >
                    Back to Login
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
        </>
      );
    }

    return (
      <>
        <Helmet>
          <meta name="robots" content="noindex, nofollow" />
          <meta name="googlebot" content="noindex, nofollow" />
        </Helmet>
        <div className="page-wraper account-wraper bg-gray" style={{ minHeight: '100vh', overflowY: 'auto', paddingTop: '50px', paddingBottom: '50px' }}>
          <div className="my-account" style={{ maxHeight: 'none' }}>
            <div className="logo">
              {isClient && <img src={logo} alt="" />}
            </div>
            
            <div className="form-group">
              <center><h4>KYC Verification - {mechanicName}</h4></center>
              <center><p style={{ fontSize: '14px', color: '#666' }}>One-time verification required</p></center>
            </div>

            {errorMessage && <div className="alert alert-danger">{errorMessage}</div>}
            {successMessage && <div className="alert alert-success">{successMessage}</div>}

            {/* STEP 1: Agreement */}
            {!agreementAccepted ? (
              <div>
                <div className="form-group">
                  <h5>Step 1: Service Agreement</h5>
                  <p style={{ fontSize: '13px', color: '#666' }}>Please read carefully (minimum 30 seconds)</p>
                </div>

                <div
                  style={{
                    border: '1px solid #ddd',
                    padding: '15px',
                    height: '300px',
                    overflowY: 'scroll',
                    backgroundColor: '#f9f9f9',
                    fontSize: '12px',
                    lineHeight: '1.8',
                    whiteSpace: 'pre-wrap'
                  }}
                  onScroll={this.startReadingTimer}
                >
                  {agreementText ? (
                    agreementText
                      .replace(/{DATE}/g, new Date().toLocaleDateString())
                      .replace(/{MECHANIC_NAME}/g, mechanicName)
                      .replace(/{MECHANIC_PHONE}/g, this.state.mechanicPhone)
                      .replace(/{ADDRESS}/g, 'As per records')
                  ) : (
                    <p style={{ textAlign: 'center', padding: '50px' }}>Loading agreement...</p>
                  )}
                </div>

                <div className="form-group" style={{ textAlign: 'center', marginTop: '10px' }}>
                  {!hasReadAgreement && (
                    <p style={{ color: '#f42222', fontSize: '14px' }}>
                      Please read for {30 - readTimeSeconds} more seconds
                    </p>
                  )}
                  {hasReadAgreement && (
                    <p style={{ color: '#28a745', fontSize: '14px' }}>
                      Reading time complete
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <button
                    className={`btn btn-lg w-100 ${hasReadAgreement ? 'btn-primary' : 'btn-secondary'}`}
                    onClick={this.handleAcceptAgreement}
                    disabled={!hasReadAgreement || uploadingAgreement}
                  >
                    {uploadingAgreement ? 'Signing Agreement...' : hasReadAgreement ? 'Click to Sign Agreement' : 'Read Agreement to Continue'}
                  </button>
                </div>
              </div>
            ) : null}

            {/* STEP 2: Document Upload (shown after agreement is signed, for KYC test phones) */}
            {agreementAccepted && !submittedForVerification ? (
              <div>
                <div className="form-group">
                  <h5>Step 2: Upload KYC Documents</h5>
                  <p style={{ fontSize: '13px', color: '#666' }}>All three documents are required</p>
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: 'bold', fontSize: '14px' }}>Aadhaar Card - Front *</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => this.handleFileChange('aadhaarFront', e)}
                    style={{ display: 'block', marginTop: '5px' }}
                  />
                  {aadhaarFront && <small style={{ color: '#28a745' }}>Selected: {aadhaarFront.name}</small>}
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: 'bold', fontSize: '14px' }}>Aadhaar Card - Back *</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => this.handleFileChange('aadhaarBack', e)}
                    style={{ display: 'block', marginTop: '5px' }}
                  />
                  {aadhaarBack && <small style={{ color: '#28a745' }}>Selected: {aadhaarBack.name}</small>}
                </div>

                <div className="form-group">
                  <label style={{ fontWeight: 'bold', fontSize: '14px' }}>PAN Card *</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => this.handleFileChange('panCard', e)}
                    style={{ display: 'block', marginTop: '5px' }}
                  />
                  {panCard && <small style={{ color: '#28a745' }}>Selected: {panCard.name}</small>}
                </div>

                <div className="form-group">
                  <button
                    className="btn btn-lg btn-primary w-100"
                    onClick={this.handleSubmitDocuments}
                    disabled={uploadingDocuments || !aadhaarFront || !aadhaarBack || !panCard}
                  >
                    {uploadingDocuments ? 'Uploading Documents...' : 'Submit Documents'}
                  </button>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </>
    );
  }
}

export default withRouter(MechanicKYC);

